import React, { ReactElement, useState, useMemo, useEffect, useRef } from 'react';
import { ClickAwayListener } from '@mui/material';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import HamburgerMenuIcon from '../halda-design-system/icons/HamburgerMenuIcon';
import { HamburgerMenuItem, ParentSidebarNavItem } from '../../../types';
import iconMap from '../../../utils/icon-map';
import { createFuzzySearcher } from '../../../utils/fuzzySearch';
import useCampaignAlertBadge from './useCampaignAlertBadge';
import CampaignAlertCountBadge from './CampaignAlertCountBadge';

const CAMPAIGN_OVERVIEW_ITEM_ID = 'campaign-overview-button-id';

interface ProfileDropdownProps {
  currentUserEmail: string;
  hamburgerItems: Array<HamburgerMenuItem>;
  navItems?: Array<ParentSidebarNavItem>;
  isInternalAdmin?: boolean;
}

interface SearchableMenuItem {
  name: string;
  href: string;
  icon: string;
  id: string;
  dataMethod?: string;
  showDividerAbove?: boolean;
  parentName?: string;
}

const FUZZY_THRESHOLD = 0.45;

const flattenNavItems = (items: Array<ParentSidebarNavItem>): Array<SearchableMenuItem> => {
  const flattened: Array<SearchableMenuItem> = [];
  items.forEach((item) => {
    const parentName = String(item.name);
    if (item.href && item.href.trim()) {
      flattened.push({
        name: parentName,
        href: item.href,
        icon: item.icon,
        id: item.id,
      });
    }
    if (item.subItems && item.subItems.length > 0) {
      item.subItems.forEach((subItem) => {
        if (subItem.href && subItem.href.trim()) {
          flattened.push({
            name: String(subItem.name),
            href: subItem.href,
            icon: subItem.icon,
            id: subItem.id,
            parentName: parentName,
          });
        }
      });
    }
  });
  return flattened;
};

const ProfileDropdown = ({
  currentUserEmail,
  hamburgerItems,
  navItems,
  isInternalAdmin = false,
}: ProfileDropdownProps): ReactElement => {
  const [show, setShow] = useState(false);
  const { count: alertCount, subscribed: alertSubscribed } = useCampaignAlertBadge({ enabled: isInternalAdmin });
  const showAlertBadge = alertSubscribed && alertCount > 0;
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && show) {
        event.preventDefault();
        setShow(false);
        setSearchQuery('');
        return;
      }

      const isMac = typeof window !== 'undefined' && /Mac|iPhone|iPod|iPad/i.test(window.navigator.platform);
      const isModifierKey = isMac ? event.metaKey : event.ctrlKey;
      const keyLower = event.key?.toLowerCase();

      if (isModifierKey && event.shiftKey && (keyLower === 'f' || event.keyCode === 70)) {
        const target = event.target as HTMLElement;
        const isInputFocused = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA';

        if (!isInputFocused) {
          event.preventDefault();
          event.stopPropagation();
          setShow(true);
          setTimeout(() => {
            searchInputRef.current?.focus();
            searchInputRef.current?.select();
          }, 0);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [show]);

  const hamburgerSearchableItems = useMemo((): Array<SearchableMenuItem> => hamburgerItems.map((item) => ({
    name: item.name,
    href: item.href,
    icon: item.icon,
    id: item.id,
    dataMethod: item.dataMethod,
    showDividerAbove: item.showDividerAbove,
  })), [hamburgerItems]);

  const navSearchableItems = useMemo(() => flattenNavItems(navItems || []), [navItems]);

  const allSearchableItems = useMemo(
    () => [...hamburgerSearchableItems, ...navSearchableItems],
    [hamburgerSearchableItems, navSearchableItems],
  );

  const searcher = useMemo(() => createFuzzySearcher(allSearchableItems, {
    keys: ['name', 'parentName'], threshold: FUZZY_THRESHOLD,
  }), [allSearchableItems]);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return hamburgerSearchableItems;
    return searcher(searchQuery);
  }, [searchQuery, hamburgerSearchableItems, searcher]);

  return (
    <div className="ml-3 relative">
      <div>
        <button
          onClick={() => {
            setShow(!show);
            if (show) {
              setSearchQuery('');
            }
          }}
          type="button"
          className="max-w-xs bg-transparent flex items-center text-sm rounded-full focus:outline-none relative"
          id="user-menu-button"
          aria-expanded="false"
          aria-haspopup="true"
        >
          <span className="sr-only">Open user menu</span>
          <HamburgerMenuIcon />
          {showAlertBadge && (
            <CampaignAlertCountBadge count={alertCount} className="absolute -top-1 -right-1" />
          )}
        </button>
      </div>

      {show && (
        <ClickAwayListener onClickAway={() => {
          setShow(false);
          setSearchQuery('');
        }}>
          <div
            ref={dropdownRef}
            className="origin-top-right absolute right-0 mt-2 w-64 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none overflow-y-auto"
            style={{ maxHeight: '90vh' }}
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="user-menu-button"
            tabIndex={-1}
          >
            <div className="flex flex-col px-4 py-2 border-solid border-0 border-b border-gray-300 text-slate-400">
              <div className="flex">Currently signed in as</div>
              <span className="text-halda-black truncate">{currentUserEmail}</span>
            </div>
            <div className="px-4 py-2 border-solid border-0 border-b border-gray-300">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Tab' && !e.shiftKey && filteredItems.length > 0) {
                      e.preventDefault();
                      const firstItemId = `dropdown-${filteredItems[0].id}`;
                      const firstItem = dropdownRef.current?.querySelector(`#${firstItemId}`) as HTMLElement;
                      firstItem?.focus();
                    }
                  }}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Search..."
                  aria-label="Search menu items"
                  autoFocus
                />
              </div>
            </div>
            {filteredItems.length > 0 ? (
              filteredItems.map((item: SearchableMenuItem, idx: number) => {
                const Icon = iconMap[item.icon as keyof typeof iconMap];
                const showDividerBelow = filteredItems[idx + 1]?.showDividerAbove;
                const isLastItem = idx === filteredItems.length - 1;
                const isFirstItem = idx === 0;

                return (
                  <a
                    key={`${item.id}-${item.href}`}
                    href={item.href}
                    onClick={() => setShow(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        const link = e.currentTarget;
                        link.click();
                        setShow(false);
                      } else if (e.key === 'Tab' && !e.shiftKey && isLastItem) {
                        e.preventDefault();
                        searchInputRef.current?.focus();
                      } else if (e.key === 'Tab' && e.shiftKey && isFirstItem) {
                        e.preventDefault();
                        searchInputRef.current?.focus();
                      }
                    }}
                    className={`flex items-center space-x-2 px-4 text-halda-black hover:bg-gray-100 focus:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${item.showDividerAbove && !isFirstItem ? 'border-0 border-t border-gray-300' : ''}`}
                    style={{ paddingTop: item.showDividerAbove && !isFirstItem ? '16px' : '8px', paddingBottom: showDividerBelow ? '16px' : '8px' }}
                    role="menuitem"
                    tabIndex={0}
                    id={`dropdown-${item.id}`}
                    data-original-id={item.id}
                    data-method={item.dataMethod}
                  >
                    {Icon && <Icon className="h-6 w-6" />}
                    <div className="flex flex-col">
                      <span>{item.name}</span>
                      {item.parentName && (
                        <span className="text-xs text-gray-500">{item.parentName}</span>
                      )}
                    </div>
                    {showAlertBadge && item.id === CAMPAIGN_OVERVIEW_ITEM_ID && (
                      <CampaignAlertCountBadge count={alertCount} className="ml-auto" />
                    )}
                  </a>
                );
              })
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No results found
              </div>
            )}
          </div>
        </ClickAwayListener>
      )}
    </div>
  );
};

export default ProfileDropdown;
